import { useSyncExternalStore } from 'react'
import { readPhase, readScene, skyFor, choosePhase, chooseScene, scatterClouds, applySky, nextPhase, SCENES } from '../lib/sky.mjs'
import { track } from '../lib/analytics'

// A tiny external store for the sky. The server snapshot is null, so SSR and
// hydration render nothing that depends on the roll; the first client read
// picks up what the head script stamped on <html>.

export { SCENES }

export const PHASE_LABELS = {
  dawn: 'dawn',
  day: 'daylight',
  golden: 'golden hour',
  dusk: 'dusk',
  night: 'night',
}

const listeners = new Set()
let snapshot = null
let started = false

function refresh() {
  const root = document.documentElement
  const sky = choosePhase(window.location.search, root, readPhase, skyFor)
  sky.scene = chooseScene(window.location.search, root, readScene)
  scatterClouds(root)
  snapshot = sky
  applySky(document, sky)
  listeners.forEach((l) => l())
}

// Once settled, the sun crawls toward whichever end of its arc is farther
// away, at a pace that would cross the whole arc in ten minutes. It moves in
// small eased steps every twenty seconds rather than one long transition: a
// transition on the sun repaints the whole scene every frame for as long as
// it runs, which is what phones cannot afford. Each step updates the stamp,
// so a scene change re-applies the current height rather than the start.
const ARC_SECONDS = 600
const STEP_SECONDS = 20
const STEP = STEP_SECONDS / ARC_SECONDS
let driftTimer = null
let driftInterval = null
function stopDrift() {
  clearTimeout(driftTimer)
  clearInterval(driftInterval)
  driftInterval = null
}
function startDrift() {
  const root = document.documentElement
  const from = parseFloat(root.getAttribute('data-sun-alt'))
  // With transitions off the crawl would be a series of jumps, so it does not start.
  const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  if (isNaN(from) || reduce) return
  const target = from < 0.5 ? 1 : 0
  const dir = target > from ? 1 : -1
  root.style.setProperty('--sun-drift', '2.4s')
  root.style.setProperty('--sun-ease', 'ease-in-out')
  clearInterval(driftInterval)
  driftInterval = setInterval(() => {
    if (document.hidden) return
    const now = parseFloat(root.getAttribute('data-sun-alt'))
    if (isNaN(now)) return stopDrift()
    const next = dir > 0 ? Math.min(target, now + STEP) : Math.max(target, now - STEP)
    root.setAttribute('data-sun-alt', next.toFixed(3))
    root.style.setProperty('--sun-alt', next.toFixed(3))
    if (snapshot) snapshot.sunAlt = next
    if (next === target) stopDrift()
  }, STEP_SECONDS * 1000)
}

// Runs fn once the web fonts are in (or soon enough that nobody waits on a
// slow connection), and then two frames later, so the reflow they cause has
// been painted before anything else starts moving.
function whenSettled(fn) {
  let done = false
  const go = () => {
    if (done) return
    done = true
    requestAnimationFrame(() => requestAnimationFrame(fn))
  }
  const fonts = document.fonts
  if (fonts && fonts.ready) fonts.ready.then(go, go)
  setTimeout(go, fonts && fonts.ready ? 2500 : 300)
}

function start() {
  if (started || typeof window === 'undefined') return
  started = true
  const root = document.documentElement
  // The head script parked the sun a little below its height. The refresh
  // re-stamps the true height, so put the parked one back before anything
  // paints; transitions are still off, so nothing jumps.
  const parked = parseFloat(root.style.getPropertyValue('--sun-alt'))
  refresh()
  const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  const entrance = !reduce && snapshot && !isNaN(parked) && Math.abs(parked - snapshot.sunAlt) > 0.001
  if (entrance) root.style.setProperty('--sun-alt', parked.toFixed(3))
  // Transitions are enabled only now, so the first paint never crossfades.
  root.setAttribute('data-live', '')
  if (!entrance) {
    driftTimer = setTimeout(startDrift, 1800)
    return
  }
  // The sun rises into place once the page has settled: the first load used
  // to start this at first paint, where it stuttered against the font swap.
  // A click in the meantime has already moved the sun, so the rise is moot.
  whenSettled(() => {
    if (sunMovedAt) return
    root.style.setProperty('--sun-drift', '1.6s')
    root.style.setProperty('--sun-ease', 'cubic-bezier(.2, .7, .25, 1)')
    root.style.setProperty('--sun-alt', snapshot.sunAlt.toFixed(3))
    driftTimer = setTimeout(startDrift, 2200)
  })
}

function subscribe(listener) {
  listeners.add(listener)
  start()
  return () => listeners.delete(listener)
}

const getSnapshot = () => snapshot
const getServerSnapshot = () => null

export function useSky() {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}

// A ?phase= or ?scene= query is for QA and would otherwise outrank the click.
function dropQuery(name) {
  try {
    const url = new URL(window.location.href)
    if (url.searchParams.has(name)) {
      url.searchParams.delete(name)
      window.history.replaceState(window.history.state, '', url)
    }
  } catch (e) { /* ignore */ }
}

// Anything that steps the hour (the sun itself, the masthead button) can
// register for the moment it happens, e.g. to play the sun's wobble.
const stepListeners = new Set()
export function onSunStep(fn) {
  stepListeners.add(fn)
  return () => stepListeners.delete(fn)
}

// When the sun last began a quick move; scene changes inside that window
// leave it alone so rapid clicks cannot bounce it up and down.
let sunMovedAt = 0
const SUN_MOVE_MS = 1000

function moveSunTo(sunAlt) {
  const root = document.documentElement
  stopDrift()
  root.style.setProperty('--sun-drift', '900ms')
  root.style.setProperty('--sun-ease', 'ease')
  root.setAttribute('data-sun-alt', sunAlt.toFixed(3))
  root.setAttribute('data-rolled', '1')
  sunMovedAt = Date.now()
  driftTimer = setTimeout(startDrift, SUN_MOVE_MS)
}

// The sun steps the hour for this page load; nothing is saved. The sun in
// the sky and the masthead icon report as separate events, since GA4 lists
// event names in its reports but not parameters.
export function stepPhase(source = 'sun') {
  const root = document.documentElement
  const from = (snapshot && snapshot.phase) || root.getAttribute('data-phase') || 'golden'
  const next = skyFor(nextPhase(from))
  track(source === 'masthead' ? 'sun_icon_click' : 'sun_click', { phase_from: from, phase_to: next.phase, theme: root.getAttribute('data-scene') || 'bay' })
  dropQuery('phase')
  root.setAttribute('data-phase', next.phase)
  moveSunTo(next.sunAlt)
  refresh()
  stepListeners.forEach((fn) => fn())
}

// The picker changes the scene for this page load only; nothing is saved.
export function setScene(name) {
  if (!SCENES.includes(name)) return
  const root = document.documentElement
  const from = root.getAttribute('data-scene') || 'bay'
  if (from !== name) track('theme_change', { theme_from: from, theme_to: name, phase: root.getAttribute('data-phase') || 'golden' })
  dropQuery('scene')
  root.setAttribute('data-scene', name)
  // A new landscape nudges the sun a little from where it is, eased like a
  // click; the crawl then resumes. If the sun is already mid-move, the
  // landscape changes but the sun stays put, so rapid clicks cannot bounce it.
  if (Date.now() - sunMovedAt >= SUN_MOVE_MS) {
    const current = parseFloat(root.getAttribute('data-sun-alt'))
    if (!isNaN(current)) {
      // A nudge of 0.06–0.16 toward whichever end of the arc has more room.
      const step = 0.06 + Math.random() * 0.1
      const towardTop = 1 - current > current
      moveSunTo(Math.max(0, Math.min(1, towardTop ? current + step : current - step)))
    }
  }
  refresh()
}
